import { useCallback } from 'react';

const useInputValidation = () => {
  const emailRegex =
    /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
  const priceRegex = /^\d+(\.\d{1,2})?$/;
  const noSpaceRegex = /^\S*$/;
  const tenDigitsRegex = /^[0-9]{10}$/;
  const numericRegex = /^[0-9]*$/;
  const alphanumericRegex = /^[0-9a-zA-Z]*$/;
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%¡.¿/&*()_+{}<>?])(?=\S{10}$).+$/;
  const urlRegex = /^https?:\/\/([\w-]+\.)+[\w-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;
  const phoneNumberRegex = /^\d{6,20}$/;
  const validateEmail = (email: any) => emailRegex.test(email);
  const validatePrice = (price: any) => priceRegex.test(price);

  const handlePrice = useCallback((e: any) => {
    const key = e.key;
    const isNumber = /^[0-9]$/.test(key);
    const isControl =
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Tab';
    const isDecimal = key === '.';

    if (!isNumber && !isControl && !isDecimal) {
      e.preventDefault();
    }
  }, []);

  const handleNoSpaces = useCallback((e: any) => {
    const key = e.key;
    const isSpace = key === ' ';

    if (isSpace) {
      e.preventDefault();
    }
  }, []);

  const handleOnlyNumbers = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Delete'
    ];

    const isNumberKey = /^[0-9]$/.test(e.key);

    if (!isNumberKey && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  }, []);

  const handleNumericInput = useCallback((e: any) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    const isCtrlCombo = e.ctrlKey || e.metaKey;

    if (
      allowedKeys.includes(e.key) ||
      (isCtrlCombo && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))
    ) {
      return;
    }

    if (!/\d/.test(e.key)) {
      e.preventDefault();
    }
  }, []);

  const handleAlphanumericInput = useCallback((e: any) => {
    if (!/[0-9a-zA-Z]/.test(e.key) && e.key !== 'Backspace') {
      e.preventDefault();
    }
    const currentValue = e.target.value.replace(/\s+/g, '');
    e.target.value = currentValue;
  }, []);

  const handleEightChars = useCallback((e: any) => {
    const maxLength = 10;
    const allowedChars = /[a-zA-Z0-9!@#$%¡¿/&*()_+{}<>?]/;

    const inputValue = e.currentTarget.value.replace(/\s+/g, '');

    if (inputValue.length >= maxLength && e.key !== 'Backspace' && e.key.length === 1) {
      e.preventDefault();
      return;
    }

    if (!allowedChars.test(e.key)) {
      e.preventDefault();
    }
  }, []);

  return {
    emailRegex,
    validatePrice,
    priceRegex,
    noSpaceRegex,
    validateEmail,
    handlePrice,
    tenDigitsRegex,
    handleOnlyNumbers,
    numericRegex,
    alphanumericRegex,
    handleNumericInput,
    handleAlphanumericInput,
    handleNoSpaces,
    handleEightChars,
    passwordRegex,
    urlRegex,
    phoneNumberRegex
  };
};

export default useInputValidation;
